/**
 * Small UI-string i18n for elements Google Translate consistently
 * misses (sidebar NavLinks, search placeholder, sign-out, dashboard
 * chrome) PLUS the Sahib auto-greet templates. Active language is
 * whatever `localStorage.ve_lang` is set to by LanguageSwitcher.
 *
 * To add a new language: add its 2-letter code + key→string map below.
 */

export function getLang() {
  if (typeof window === 'undefined') return 'en';
  return localStorage.getItem('ve_lang') || 'en';
}

// Every key MUST have an English entry. Translations are best-effort —
// missing keys gracefully fall back to English.
const STRINGS = {
  en: {
    'nav.overview':         'Overview',
    'nav.founder':          'My Journey',
    'nav.shura':            'Shura Nexus',
    'nav.entities_live':    'Live Entity Map',
    'nav.entities_os':      'Ruʾya — Entity Portfolio',
    'nav.opportunities':    'Opportunities CRM',
    'nav.approvals':        'Approval Center',
    'nav.founder_vetting':  'Founder Vetting',
    'nav.founders':         'Founders',
    'nav.minutes':          'Board Minutes',
    'nav.startups':         'Startups (Vetting)',
    'nav.investors':        'Investor Portal',
    'nav.chat':             'Compliance Chat',
    'nav.meetings':         'Meetings & Rooms',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'Documents · OCR',
    'nav.events':           'Events',
    'nav.ai_employees':     'AI Employees',
    'nav.ai_mentors':       'AI Mentors',
    'nav.ai_council':       'AI Council',
    'nav.ai_drafts':        'AI Drafts',
    'nav.erm':              'AI Risk Essentials',
    'nav.help':             'Help & Guides',
    'nav.manual':           'Operating Manual',
    'nav.api_docs':         'API Docs',
    'nav.audit':            'Audit Log',
    'nav.change_requests':  'Feedback Journal',
    'nav.security':         'Security & DR',
    'nav.tenant':           'Tenant & Brand',
    'nav.billing':          'Bank & Billing',
    'nav.users':            'User Management',
    'nav.roles':            'Role Builder',
    'header.search':        'Search…',
    'header.signed_in':     'Signed in',
    'header.real':          'Real',
    'header.preview':       '· preview',
    'header.notifications': 'Notifications',
    'btn.sign_out':         'Sign out',
    'btn.pto':              'PTO mode',
    'btn.pto_on':           'PTO mode ON — digest paused',
    'btn.pto_off':          'PTO mode off — digest resumed',
    // Sovereignty banner (public, every marketing page)
    'sov.eyebrow':          'Sovereign AI · Zero-Leak Architecture',
    'sov.body':             'Your AI is sovereign to your tenancy. All queries are masked, never sent back for training, and your trade secrets never leave the cluster.',
  },
  ar: {
    // Sovereignty banner — Arabic
    'sov.eyebrow':          'ذكاء اصطناعي سيادي · هندسة بلا تسريب',
    'sov.body':             'ذكاؤك الاصطناعي مِلكٌ لمؤسستك وحدها. تُقنَّع كل الاستعلامات قبل خروجها، ولا تُستخدم في تدريب نماذج عامة، وأسرارك التجارية لا تغادر بيئتك أبدًا.',
    'nav.overview':         'نظرة عامة',
    'nav.founder':          'رحلة المؤسس',
    'nav.entities_live':    'خريطة الكيانات الحية',
    'nav.entities_os':      'رؤيا — محفظة الكيانات',
    'nav.opportunities':    'إدارة الفرص',
    'nav.approvals':        'مركز الموافقات',
    'nav.founder_vetting':  'تقييم المؤسسين',
    'nav.minutes':          'محاضر مجلس الإدارة',
    'nav.startups':         'الشركات الناشئة (التحقّق)',
    'nav.investors':        'بوابة المستثمرين',
    'nav.chat':             'محادثة الامتثال',
    'nav.meetings':         'الاجتماعات والقاعات',
    'nav.agreements':       'اتفاقيات السرية والتفاهم',
    'nav.documents':        'الوثائق · OCR',
    'nav.events':           'الفعاليات',
    'nav.ai_employees':     'موظفو الذكاء الاصطناعي',
    'nav.ai_mentors':       'مرشدو الذكاء الاصطناعي',
    'nav.ai_council':       'مجلس الذكاء الاصطناعي',
    'nav.ai_drafts':        'مسودات الذكاء الاصطناعي',
    'nav.erm':              'إدارة المخاطر بالذكاء الاصطناعي',
    'nav.help':             'الدليل والمساعدة',
    'nav.api_docs':         'وثائق الـ API',
    'nav.audit':            'سجلّ المراجعة',
    'nav.change_requests':  'يوميات الملاحظات',
    'nav.security':         'الأمن والتعافي',
    'nav.tenant':           'المستأجر والعلامة',
    'nav.billing':          'البنوك والفوترة',
    'nav.users':            'إدارة المستخدمين',
    'header.search':        'بحث…',
    'header.signed_in':     'تسجيل الدخول',
    'header.real':          'فعلي',
    'header.preview':       '· معاينة',
    'header.notifications': 'الإشعارات',
    'btn.sign_out':         'تسجيل الخروج',
    'btn.pto':              'وضع الإجازة',
    'btn.pto_on':           'وضع الإجازة مُفعَّل — تم إيقاف الموجز',
    'btn.pto_off':          'وضع الإجازة موقوف — استؤنف الموجز',
  },
  ur: {
    'nav.overview':         'جائزہ',
    'nav.founder':          'فاؤنڈر سفر',
    'nav.entities_live':    'لائیو ادارہ نقشہ',
    'nav.entities_os':      'رؤیا — ادارہ پورٹ فولیو',
    'nav.opportunities':    'مواقع CRM',
    'nav.approvals':        'منظوری مرکز',
    'nav.minutes':          'بورڈ منٹس',
    'nav.startups':         'اسٹارٹ اپس (جانچ)',
    'nav.investors':        'سرمایہ کار پورٹل',
    'nav.chat':             'تعمیل چیٹ',
    'nav.meetings':         'میٹنگز اور کمرے',
    'nav.agreements':       'NDA اور MOU',
    'nav.documents':        'دستاویزات · OCR',
    'nav.events':           'تقریبات',
    'nav.ai_employees':     'AI ملازمین',
    'nav.ai_mentors':       'AI مرشد',
    'nav.ai_council':       'AI کونسل',
    'nav.ai_drafts':        'AI مسودات',
    'nav.erm':              'AI رسک',
    'nav.help':             'مدد و رہنمائی',
    'nav.api_docs':         'API دستاویزات',
    'nav.audit':            'آڈٹ لاگ',
    'nav.change_requests':  'فیڈبیک جرنل',
    'nav.security':         'سیکیورٹی و DR',
    'nav.tenant':           'کرایہ دار اور برانڈ',
    'nav.billing':          'بینک اور بلنگ',
    'nav.users':            'صارف انتظام',
    'header.search':        'تلاش کریں…',
    'btn.sign_out':         'سائن آؤٹ',
  },
  fr: {
    'nav.overview':         'Vue d\'ensemble',
    'nav.founder':          'Parcours fondateur',
    'nav.entities_live':    'Carte des entités',
    'nav.entities_os':      'Ruʾya — Portefeuille d\'entités',
    'nav.opportunities':    'CRM Opportunités',
    'nav.approvals':        'Centre d\'approbation',
    'nav.minutes':          'Procès-verbaux',
    'nav.startups':         'Startups (Examen)',
    'nav.investors':        'Portail investisseur',
    'nav.chat':             'Chat de conformité',
    'nav.meetings':         'Réunions & Salles',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'Documents · OCR',
    'nav.events':           'Événements',
    'nav.ai_employees':     'Employés IA',
    'nav.ai_mentors':       'Mentors IA',
    'nav.ai_council':       'Conseil IA',
    'nav.ai_drafts':        'Brouillons IA',
    'nav.erm':              'Risques IA',
    'nav.help':             'Aide & Guides',
    'nav.api_docs':         'Documentation API',
    'nav.audit':            'Journal d\'audit',
    'nav.change_requests':  'Journal de retours',
    'nav.security':         'Sécurité & DR',
    'nav.tenant':           'Tenant & Marque',
    'nav.billing':          'Banque & Facturation',
    'nav.users':            'Gestion utilisateurs',
    'header.search':        'Rechercher…',
    'btn.sign_out':         'Se déconnecter',
  },
  es: {
    'nav.overview':         'Resumen',
    'nav.founder':          'Recorrido del fundador',
    'nav.entities_live':    'Mapa de entidades',
    'nav.entities_os':      'Ruʾya — Cartera de entidades',
    'nav.opportunities':    'CRM de oportunidades',
    'nav.approvals':        'Centro de aprobación',
    'nav.minutes':          'Actas',
    'nav.startups':         'Startups (Análisis)',
    'nav.investors':        'Portal del inversor',
    'nav.chat':             'Chat de cumplimiento',
    'nav.meetings':         'Reuniones & Salas',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'Documentos · OCR',
    'nav.events':           'Eventos',
    'nav.ai_employees':     'Empleados IA',
    'nav.ai_mentors':       'Mentores IA',
    'nav.ai_council':       'Consejo IA',
    'nav.ai_drafts':        'Borradores IA',
    'nav.erm':              'Riesgos IA',
    'nav.help':             'Ayuda y Guías',
    'nav.api_docs':         'Documentación API',
    'nav.audit':            'Registro de auditoría',
    'nav.change_requests':  'Diario de comentarios',
    'nav.security':         'Seguridad y DR',
    'nav.tenant':           'Inquilino y Marca',
    'nav.billing':          'Banco y Facturación',
    'nav.users':            'Gestión de usuarios',
    'header.search':        'Buscar…',
    'btn.sign_out':         'Cerrar sesión',
  },
  de: {
    'nav.overview':         'Übersicht',
    'nav.founder':          'Gründerreise',
    'nav.entities_live':    'Live-Entitätskarte',
    'nav.entities_os':      'Ruʾya — Entitätenportfolio',
    'nav.opportunities':    'Opportunitäten-CRM',
    'nav.approvals':        'Genehmigungszentrum',
    'nav.minutes':          'Sitzungsprotokolle',
    'nav.startups':         'Startups (Prüfung)',
    'nav.investors':        'Investorenportal',
    'nav.chat':             'Compliance-Chat',
    'nav.meetings':         'Meetings & Räume',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'Dokumente · OCR',
    'nav.events':           'Veranstaltungen',
    'nav.ai_employees':     'KI-Mitarbeiter',
    'nav.ai_mentors':       'KI-Mentoren',
    'nav.ai_council':       'KI-Rat',
    'nav.ai_drafts':        'KI-Entwürfe',
    'nav.erm':              'KI-Risiken',
    'nav.help':             'Hilfe & Anleitungen',
    'nav.api_docs':         'API-Dokumentation',
    'nav.audit':            'Audit-Log',
    'nav.change_requests':  'Feedback-Journal',
    'nav.security':         'Sicherheit & DR',
    'nav.tenant':           'Mandant & Marke',
    'nav.billing':          'Bank & Abrechnung',
    'nav.users':            'Benutzerverwaltung',
    'header.search':        'Suchen…',
    'btn.sign_out':         'Abmelden',
  },
  'zh-CN': {
    'nav.overview':         '概览',
    'nav.founder':          '创始人之旅',
    'nav.entities_live':    '实时实体图',
    'nav.entities_os':      'Ruʾya — 实体投资组合',
    'nav.opportunities':    '机会 CRM',
    'nav.approvals':        '审批中心',
    'nav.minutes':          '董事会会议记录',
    'nav.startups':         '初创公司（审查）',
    'nav.investors':        '投资者门户',
    'nav.chat':             '合规聊天',
    'nav.meetings':         '会议与会议室',
    'nav.agreements':       'NDA 与 MOU',
    'nav.documents':        '文档 · OCR',
    'nav.events':           '活动',
    'nav.ai_employees':     'AI 员工',
    'nav.ai_mentors':       'AI 导师',
    'nav.ai_council':       'AI 委员会',
    'nav.ai_drafts':        'AI 草稿',
    'nav.erm':              'AI 风险要点',
    'nav.help':             '帮助与指南',
    'nav.api_docs':         'API 文档',
    'nav.audit':            '审计日志',
    'nav.change_requests':  '反馈日志',
    'nav.security':         '安全与灾难恢复',
    'nav.tenant':           '租户与品牌',
    'nav.billing':          '银行与账单',
    'nav.users':            '用户管理',
    'header.search':        '搜索…',
    'btn.sign_out':         '退出登录',
  },
  hi: {
    'nav.overview':         'अवलोकन',
    'nav.founder':          'संस्थापक यात्रा',
    'nav.entities_live':    'लाइव संस्था मानचित्र',
    'nav.entities_os':      'रुऔया — संस्था पोर्टफोलियो',
    'nav.opportunities':    'अवसर CRM',
    'nav.approvals':        'अनुमोदन केंद्र',
    'nav.minutes':          'बोर्ड मिनट्स',
    'nav.startups':         'स्टार्टअप (समीक्षा)',
    'nav.investors':        'निवेशक पोर्टल',
    'nav.chat':             'अनुपालन चैट',
    'nav.meetings':         'मीटिंग और कक्ष',
    'nav.agreements':       'NDA और MOU',
    'nav.documents':        'दस्तावेज़ · OCR',
    'nav.events':           'कार्यक्रम',
    'nav.ai_employees':     'AI कर्मचारी',
    'nav.ai_mentors':       'AI मेंटर',
    'nav.ai_council':       'AI परिषद',
    'nav.ai_drafts':        'AI मसौदे',
    'nav.erm':              'AI जोखिम',
    'nav.help':             'सहायता और गाइड',
    'nav.api_docs':         'API दस्तावेज़',
    'nav.audit':            'ऑडिट लॉग',
    'nav.change_requests':  'प्रतिक्रिया जर्नल',
    'nav.security':         'सुरक्षा और DR',
    'nav.tenant':           'किरायेदार और ब्रांड',
    'nav.billing':          'बैंक और बिलिंग',
    'nav.users':            'उपयोगकर्ता प्रबंधन',
    'header.search':        'खोजें…',
    'btn.sign_out':         'साइन आउट',
  },
  tr: {
    'nav.overview':         'Genel Bakış',
    'nav.founder':          'Kurucu Yolculuğu',
    'nav.entities_live':    'Canlı Kuruluş Haritası',
    'nav.entities_os':      'Ruʾya — Kuruluş Portföyü',
    'nav.opportunities':    'Fırsatlar CRM',
    'nav.approvals':        'Onay Merkezi',
    'nav.minutes':          'Yönetim Kurulu Tutanakları',
    'nav.startups':         'Girişimler (İnceleme)',
    'nav.investors':        'Yatırımcı Portalı',
    'nav.chat':             'Uyumluluk Sohbeti',
    'nav.meetings':         'Toplantılar ve Odalar',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'Belgeler · OCR',
    'nav.events':           'Etkinlikler',
    'nav.ai_employees':     'AI Çalışanlar',
    'nav.ai_mentors':       'AI Mentorlar',
    'nav.ai_council':       'AI Konseyi',
    'nav.ai_drafts':        'AI Taslaklar',
    'nav.erm':              'AI Risk',
    'nav.help':             'Yardım ve Kılavuzlar',
    'nav.api_docs':         'API Belgeleri',
    'nav.audit':            'Denetim Kaydı',
    'nav.change_requests':  'Geri Bildirim Günlüğü',
    'nav.security':         'Güvenlik ve DR',
    'nav.tenant':           'Kiracı ve Marka',
    'nav.billing':          'Banka ve Faturalama',
    'nav.users':            'Kullanıcı Yönetimi',
    'header.search':        'Girişim, kurucu, sözleşme ara…',
    'btn.sign_out':         'Çıkış',
  },
  ru: {
    'nav.overview':         'Обзор',
    'nav.founder':          'Путь основателя',
    'nav.entities_live':    'Карта компаний в реальном времени',
    'nav.entities_os':      'Ruʾya — Портфель компаний',
    'nav.opportunities':    'CRM возможностей',
    'nav.approvals':        'Центр согласований',
    'nav.minutes':          'Протоколы заседаний',
    'nav.startups':         'Стартапы (Проверка)',
    'nav.investors':        'Портал инвестора',
    'nav.chat':             'Чат соответствия',
    'nav.meetings':         'Встречи и комнаты',
    'nav.agreements':       'NDA и MOU',
    'nav.documents':        'Документы · OCR',
    'nav.events':           'Мероприятия',
    'nav.ai_employees':     'AI-сотрудники',
    'nav.ai_mentors':       'AI-наставники',
    'nav.ai_council':       'AI-совет',
    'nav.ai_drafts':        'AI-черновики',
    'nav.erm':              'AI-риски',
    'nav.help':             'Помощь и руководства',
    'nav.api_docs':         'API-документация',
    'nav.audit':            'Журнал аудита',
    'nav.change_requests':  'Журнал отзывов',
    'nav.security':         'Безопасность и DR',
    'nav.tenant':           'Тенант и Бренд',
    'nav.billing':          'Банк и Биллинг',
    'nav.users':            'Управление пользователями',
    'header.search':        'Искать стартапы, основателей, соглашения…',
    'btn.sign_out':         'Выйти',
  },
  ja: {
    'nav.overview':         '概要',
    'nav.founder':          '創業者の旅',
    'nav.entities_live':    'ライブ・エンティティマップ',
    'nav.entities_os':      'Ruʾya — エンティティ・ポートフォリオ',
    'nav.opportunities':    'オポチュニティCRM',
    'nav.approvals':        '承認センター',
    'nav.minutes':          '取締役会議事録',
    'nav.startups':         'スタートアップ（審査）',
    'nav.investors':        '投資家ポータル',
    'nav.chat':             'コンプライアンスチャット',
    'nav.meetings':         '会議＆ルーム',
    'nav.agreements':       'NDA & MOU',
    'nav.documents':        'ドキュメント · OCR',
    'nav.events':           'イベント',
    'nav.ai_employees':     'AI従業員',
    'nav.ai_mentors':       'AIメンター',
    'nav.ai_council':       'AI評議会',
    'nav.ai_drafts':        'AIドラフト',
    'nav.erm':              'AIリスク',
    'nav.help':             'ヘルプ＆ガイド',
    'nav.api_docs':         'APIドキュメント',
    'nav.audit':            '監査ログ',
    'nav.change_requests':  'フィードバックジャーナル',
    'nav.security':         'セキュリティ & DR',
    'nav.tenant':           'テナント & ブランド',
    'nav.billing':          '銀行 & 請求',
    'nav.users':            'ユーザー管理',
    'header.search':        '検索…',
    'btn.sign_out':         'サインアウト',
  },
};

export function t(key) {
  const lang = getLang();
  const bag = STRINGS[lang] || STRINGS.en;
  return bag[key] || STRINGS.en[key] || key;
}

export function isRtl() {
  const lang = getLang();
  return lang === 'ar' || lang === 'ur';
}

// ─────────────────────────────────────────────────────────────────────────
//  Sahib auto-greet — contextual opening line in the user's language.
//  Returns a single string: "<greeting>, <self-intro>. <page-aware offer>"
//  Falls back gracefully — every persona × language has a sensible default.
// ─────────────────────────────────────────────────────────────────────────
const GREETING_BY_LANG = {
  en: { hi: 'Hello', who: 'I am', help: 'How can I help you with' },
  ar: { hi: 'السلام عليكم، أنا', who: '', help: 'كيف يمكنني مساعدتك في' },
  ur: { hi: 'السلام علیکم، میں', who: '', help: 'میں آپ کی مدد کیسے کر سکتا/سکتی ہوں' },
  fr: { hi: 'Bonjour, je suis', who: '', help: 'Comment puis-je vous aider concernant' },
  es: { hi: 'Hola, soy', who: '', help: '¿Cómo puedo ayudarle con' },
  de: { hi: 'Hallo, ich bin', who: '', help: 'Wie kann ich Ihnen helfen mit' },
  'zh-CN': { hi: '您好，我是', who: '', help: '我能在' },
  hi: { hi: 'नमस्ते, मैं', who: '', help: 'मैं आपकी कैसे मदद कर सकता/सकती हूँ' },
  tr: { hi: 'Merhaba, ben', who: '', help: 'Size nasıl yardımcı olabilirim' },
  ru: { hi: 'Здравствуйте, я', who: '', help: 'Чем я могу помочь с' },
  ja: { hi: 'こんにちは、私は', who: '', help: 'お手伝いできることは' },
};

export function buildGreeting(personaEnglishName, personaArabicName, personaRole, pageLabel) {
  const lang = getLang();
  const g = GREETING_BY_LANG[lang] || GREETING_BY_LANG.en;
  const name = (lang === 'ar' || lang === 'ur') ? personaArabicName || personaEnglishName : personaEnglishName;
  // Closing punctuation per language
  if (lang === 'ar') {
    return `${g.hi} ${name} (${personaRole}). ${g.help} ${pageLabel || 'صفحتك'}؟`;
  }
  if (lang === 'ur') {
    return `${g.hi} ${name} (${personaRole}). ${g.help} ${pageLabel || 'صفحے'} میں؟`;
  }
  if (lang === 'zh-CN') {
    return `${g.hi}${name}（${personaRole}）。我能在${pageLabel || '此页面'}为您提供什么帮助？`;
  }
  if (lang === 'ja') {
    return `${g.hi}${name}（${personaRole}）です。${pageLabel || 'こちら'}で${g.help}ありますか？`;
  }
  if (lang === 'hi') {
    return `${g.hi} ${name} (${personaRole}) हूँ। ${pageLabel || 'इस पेज'} पर ${g.help}?`;
  }
  if (lang === 'tr') {
    return `${g.hi} ${name} (${personaRole}). ${pageLabel || 'bu sayfa'} hakkında ${g.help}?`;
  }
  if (lang === 'ru') {
    return `${g.hi} ${name} (${personaRole}). ${g.help} ${pageLabel || 'этой страницей'}?`;
  }
  if (lang === 'fr' || lang === 'es' || lang === 'de') {
    return `${g.hi} ${name} (${personaRole}). ${g.help} ${pageLabel || 'cette page'}?`;
  }
  // Default English
  return `${g.hi} ${name}, ${personaRole}. ${g.help} ${pageLabel || 'this page'}?`;
}
